import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "/logo.png";

/**
 * The screen a handed-out password leads to.
 *
 * An account carrying `must_change_password` reaches nothing else — the API
 * answers `password_change_required` to every other call — so this is where the
 * router sends it until a password of the user's own is set.
 */
export default function ChangePasswordPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      toast.error('Enter your current and new password');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('The two new passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // the change retires every earlier token, this session's included
      if (res.data?.token) localStorage.setItem('labelify_token', res.data.token);
      await checkAuth();
      toast.success('Password changed');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change the password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50/50 p-6" data-testid="change-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Labelify Private Limited" className="h-16 w-auto mx-auto object-contain" />
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-card border border-slate-100 p-8">
          <div className="mb-6 flex items-start gap-3">
            <KeyRound className="h-5 w-5 mt-1 text-brand-blue" />
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-slate-900">Set a new password</h2>
              <p className="text-sm text-slate-500 mt-1 font-body">
                {user?.full_name ? `${user.full_name}, your` : 'Your'} password was issued to you.
                Choose your own before you carry on.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="current-password">Current password</Label>
              <Input id="current-password" type="password" value={currentPassword}
                data-testid="current-password-input"
                onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <div>
              <Label htmlFor="new-password">New password</Label>
              <Input id="new-password" type="password" value={newPassword}
                data-testid="new-password-input"
                onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input id="confirm-password" type="password" value={confirmPassword}
                data-testid="confirm-password-input"
                onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
            </div>
          </div>

          <Button type="submit" className="w-full mt-6" disabled={loading} data-testid="change-password-submit">
            {loading ? 'Saving…' : 'Change password'}
          </Button>
          <Button type="button" variant="ghost" className="w-full mt-2" onClick={logout}>
            Sign out
          </Button>
        </form>
      </div>
    </div>
  );
}
